package fr.manyverse.app.presentation.components.modules.whiteboard

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.gestures.detectDragGestures
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.BlendMode
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Paint
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.StrokeJoin
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.drawscope.drawIntoCanvas
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.onSizeChanged
import fr.manyverse.app.domain.model.DrawPoint
import fr.manyverse.app.domain.model.WhiteboardAction
import fr.manyverse.app.domain.model.WhiteboardColor

/**
 * Canvas component for drawing on the whiteboard.
 */
@Composable
internal fun WhiteboardCanvas(
    strokes: List<WhiteboardAction.Stroke>,
    showGrid: Boolean,
    isDrawingEnabled: Boolean,
    currentColor: WhiteboardColor,
    currentStrokeWidth: Float,
    currentTool: WhiteboardTool,
    onStrokeComplete: (List<DrawPoint>) -> Unit,
    modifier: Modifier = Modifier
) {
    val currentPath = remember { mutableStateListOf<Offset>() }
    var canvasSize by remember { mutableStateOf(Size.Zero) }

    val backgroundColor = Color.White
    val gridColor = Color.LightGray.copy(alpha = 0.5f)
    val eraserCursorColor = Color.Gray

    // The eraser draws in the background color, with its own width
    val drawColor = Color(currentTool.strokeColor(currentColor).hexValue)
    val drawStrokeWidth = currentTool.strokeWidth(currentStrokeWidth)

    val gridPath = remember(showGrid, canvasSize) {
        if (!showGrid || canvasSize == Size.Zero) {
            null
        } else {
            Path().apply {
                val gridSpacing = 50f
                var x = gridSpacing
                while (x < canvasSize.width) {
                    moveTo(x, 0f)
                    lineTo(x, canvasSize.height)
                    x += gridSpacing
                }
                var y = gridSpacing
                while (y < canvasSize.height) {
                    moveTo(0f, y)
                    lineTo(canvasSize.width, y)
                    y += gridSpacing
                }
            }
        }
    }

    val strokePaths = remember(strokes, canvasSize) {
        if (canvasSize == Size.Zero) {
            emptyList()
        } else {
            strokes.mapNotNull { stroke ->
                if (stroke.points.size < 2) return@mapNotNull null
                val path = Path().apply {
                    val first = stroke.points.first()
                    moveTo(first.x * canvasSize.width, first.y * canvasSize.height)
                    for (i in 1 until stroke.points.size) {
                        val p = stroke.points[i]
                        lineTo(p.x * canvasSize.width, p.y * canvasSize.height)
                    }
                }
                stroke to path
            }
        }
    }

    Canvas(
        modifier = modifier
            .fillMaxSize()
            .background(backgroundColor)
            .onSizeChanged { canvasSize = Size(it.width.toFloat(), it.height.toFloat()) }
            .then(
                if (isDrawingEnabled) {
                    Modifier.pointerInput(currentColor, currentStrokeWidth, currentTool) {
                        detectDragGestures(
                            onDragStart = { offset ->
                                currentPath.clear()
                                currentPath.add(offset)
                            },
                            onDrag = { change, _ ->
                                change.consume()
                                currentPath.add(change.position)
                            },
                            onDragEnd = {
                                if (currentPath.isNotEmpty() && canvasSize.width > 0 && canvasSize.height > 0) {
                                    val normalizedPoints = currentPath.map { offset ->
                                        DrawPoint(
                                            x = offset.x / canvasSize.width,
                                            y = offset.y / canvasSize.height
                                        )
                                    }
                                    onStrokeComplete(normalizedPoints)
                                }
                                currentPath.clear()
                            },
                            onDragCancel = {
                                currentPath.clear()
                            }
                        )
                    }
                } else {
                    Modifier
                }
            )
    ) {
        gridPath?.let {
            drawPath(
                path = it,
                color = gridColor,
                style = Stroke(width = 1f)
            )
        }

        // Strokes live in their own layer: the eraser clears that layer instead of
        // painting over it, so the background and the grid below stay untouched.
        drawIntoCanvas { canvas ->
            canvas.saveLayer(Rect(Offset.Zero, size), Paint())

            strokePaths.forEach { (stroke, path) ->
                drawPath(
                    path = path,
                    color = Color(stroke.color.hexValue),
                    style = Stroke(
                        width = stroke.strokeWidth,
                        cap = StrokeCap.Round,
                        join = StrokeJoin.Round
                    ),
                    blendMode = if (stroke.isEraser) BlendMode.Clear else BlendMode.SrcOver
                )
            }

            // Note: Text elements are rendered as an overlay composable
            // since Canvas text rendering is limited in Compose Multiplatform

            if (currentPath.size >= 2) {
                val path = Path().apply {
                    moveTo(currentPath.first().x, currentPath.first().y)
                    for (i in 1 until currentPath.size) {
                        lineTo(currentPath[i].x, currentPath[i].y)
                    }
                }
                drawPath(
                    path = path,
                    color = drawColor,
                    style = Stroke(
                        width = drawStrokeWidth,
                        cap = StrokeCap.Round,
                        join = StrokeJoin.Round
                    ),
                    blendMode = if (currentTool == WhiteboardTool.ERASER) {
                        BlendMode.Clear
                    } else {
                        BlendMode.SrcOver
                    }
                )
            }

            canvas.restore()
        }

        // Eraser cursor: same size as the stroke it leaves behind
        if (currentTool == WhiteboardTool.ERASER) {
            currentPath.lastOrNull()?.let { position ->
                drawCircle(
                    color = eraserCursorColor,
                    radius = drawStrokeWidth / 2f,
                    center = position,
                    style = Stroke(width = 1f)
                )
            }
        }
    }
}
